// src/pages/ProfilePage.jsx
import React, { useEffect, useState } from "react";
import { getFavourites, removeFavourite } from "../api/favourites";
import { Link } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";

export default function ProfilePage() {
  const { user, token, loading: authLoading } = useAuth();
  const [favourites, setFavourites] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!token) {
      setFavourites([]);
      setLoadingFavs(false);
      return;
    }

    setLoadingFavs(true);
    setError(null);

    getFavourites()
      .then((data) => {
        setFavourites(data.favourites || []);
      })
      .catch((err) => {
        console.error("Failed to fetch favourites:", err);
        setError("Could not load your favourites.");
      })
      .finally(() => {
        setLoadingFavs(false);
      });
  }, [token]);

  const handleRemove = async (carId) => {
    setRemovingId(carId);
    try {
      await removeFavourite(carId);
      setFavourites((prev) => prev.filter((v) => v._id !== carId));
    } catch (err) {
      console.error("Failed to remove favourite:", err);
    } finally {
      setRemovingId(null);
    }
  };

  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!token) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <p className="text-xl text-danger mb-4">
          You need to be logged in to view your profile.
        </p>
        <Link
          to="/signin"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Go to Login
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-32 space-y-12 bg-primary pb-80 -mb-80">
      <section className="bg-surface rounded-lg shadow p-6">
        <h2 className="text-2xl font-semibold mb-4">My Profile</h2>
        <div className="space-y-2 text-sm">
          <div className="flex">
            <span className="w-32 font-medium">Full Name:</span>
            <span>{user.fullName}</span>
          </div>
          <div className="flex">
            <span className="w-32 font-medium">Email:</span>
            <span>{user.email}</span>
          </div>
          {user.phone && (
            <div className="flex">
              <span className="w-32 font-medium">Phone:</span>
              <span>{user.phone}</span>
            </div>
          )}
          <div className="flex">
            <span className="w-32 font-medium">Joined:</span>
            <span>
              {new Date(user.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4">My Favourite Cars</h2>

        {loadingFavs ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="text-danger">{error}</p>
        ) : favourites.length === 0 ? (
          <p className="text-secondary-accent">
            You have no favourite cars yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favourites.map((car) => (
              <FavouriteCarCard
                key={car._id}
                car={car}
                onRemove={() => handleRemove(car._id)}
                removing={removingId === car._id}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FavouriteCarCard({ car, onRemove, removing }) {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200 flex flex-col group">
      {/* Image at top */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={car.thumbnail || "/placeholder.jpg"}
          alt={`${car.make} ${car.model}`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
        />
      </div>

      {/* Content section */}
      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-4">
          <h3 className="text-primary text-lg font-semibold">
            {car.make} {car.model}
          </h3>
          <p className="mt-1 text-sm text-surface">
            £{car?.price ? car.price.toLocaleString() : "No price"}
          </p>
        </div>

        {/* Bottom row always pushed to bottom via mt-auto */}
        <div className="mt-auto flex justify-between items-center border-t border-gray-200 pt-3">
          <Link
            to={`/car/${car._id}`}
            className="text-accent hover:underline text-sm font-medium"
          >
            View Details
          </Link>

          <button
            onClick={onRemove}
            disabled={removing}
            className={`inline-flex items-center space-x-1 text-sm font-medium px-2 py-1 rounded ${
              removing
                ? "bg-red-100 text-red-300 cursor-not-allowed"
                : "bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800"
            } transition-colors duration-150`}
          >
            <FaTrashAlt className="h-4 w-4" />
            <span>{removing ? "Removing…" : "Remove"}</span>
          </button>
        </div>
      </div>
    </div>
    // <div className="border rounded-lg overflow-hidden bg-white shadow flex flex-col">
    //   {/* Image at top */}
    //   <div className="relative w-full aspect-[4/3] bg-gray-100">
    //     <img
    //       src={car.thumbnail || "/placeholder.jpg"}
    //       alt={`${car.make} ${car.model}`}
    //       className="absolute inset-0 w-full h-full object-cover"
    //     />
    //   </div>

    //   {/* Content section (text + bottom row) */}
    //   <div className="p-4 flex flex-col flex-grow">
    //     <div>
    //       <h3 className="text-accent text-lg font-bold mb-1">
    //         {car.make} {car.model}
    //       </h3>
    //       <p className="text-sm text-primary mb-2">
    //         £{car.price.toLocaleString()}
    //       </p>
    //     </div>

    //     {/* Bottom row always pushed to bottom via mt-auto */}
    //     <div className="mt-auto flex justify-between items-center">
    //       <Link
    //         to={`/car/${car._id}`}
    //         className="text-blue-600 hover:underline text-sm"
    //       >
    //         View Details
    //       </Link>
    //       <button
    //         onClick={onRemove}
    //         disabled={removing}
    //         className={`inline-flex items-center space-x-1 text-sm ${
    //           removing
    //             ? "opacity-50 cursor-not-allowed"
    //             : "text-red-600 hover:text-red-800"
    //         }`}
    //       >
    //         <FaTrashAlt className="h-4 w-4" />
    //         <span>{removing ? "Removing…" : "Remove"}</span>
    //       </button>
    //     </div>
    //   </div>
    // </div>
  );
}
